import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Send, Users, Clock, CheckCircle, XCircle, 
  Filter, Eye, Trash2, Plus, RefreshCw, AlertTriangle,
  TrendingUp, UserX
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEmailCampaigns() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sending, setSending] = useState({});

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = statusFilter 
        ? `${API_URL}/api/admin/campaigns?status=${statusFilter}`
        : `${API_URL}/api/admin/campaigns`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [fetchCampaigns, fetchStats]);

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign? This action cannot be undone.')) {
      return;
    }

    setSending(prev => ({ ...prev, [campaignId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: "Campaign Sending",
          description: "Email campaign is being sent in the background.",
        });
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to send campaign",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send campaign",
        variant: "destructive"
      });
    } finally {
      setSending(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: "Deleted",
          description: "Campaign deleted successfully",
        });
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to delete campaign",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete campaign",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sending: 'bg-blue-100 text-blue-700 animate-pulse',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    
    const icons = {
      draft: <Clock className="w-3 h-3" />,
      sending: <RefreshCw className="w-3 h-3 animate-spin" />,
      sent: <CheckCircle className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${styles[status] || styles.draft}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getAudienceLabel = (type) => {
    const labels = {
      all_users: 'All Users',
      partners_only: 'Partners Only',
      customers_with_orders: 'Customers with Orders',
      custom: 'Custom Filter'
    };
    return labels[type] || type;
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0d0d0d]">Email Campaigns</h1>
            <p className="text-[#737373] mt-1">Send email blasts to users and partners</p>
          </div>
          <Button 
            onClick={() => navigate('/admin/campaigns/new')}
            className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#D4AF37]/10 rounded">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm text-[#737373]">Total Campaigns</p>
                  <p className="text-xl font-bold text-[#0d0d0d]">{stats.total_campaigns}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded">
                  <Send className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-[#737373]">Campaigns Sent</p>
                  <p className="text-xl font-bold text-[#0d0d0d]">{stats.sent_campaigns}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#737373]">Total Emails Sent</p>
                  <p className="text-xl font-bold text-[#0d0d0d]">{stats.total_emails_sent.toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded">
                  <UserX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-[#737373]">Unsubscribed</p>
                  <p className="text-xl font-bold text-[#0d0d0d]">{stats.unsubscribed_count}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rate Limit Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Rate Limit Active</p>
            <p className="text-sm text-amber-700">Maximum 2 emails per user per week to prevent spam. Unsubscribed users are automatically excluded.</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#737373]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[#e5e5e5] rounded px-3 py-2 text-sm"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="sending">Sending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCampaigns}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Campaigns Table */}
        <div className="bg-white rounded-lg border border-[#e5e5e5] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#737373]">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 text-[#d4d4d4] mx-auto mb-4" />
              <p className="text-[#737373]">No campaigns yet</p>
              <Button 
                onClick={() => navigate('/admin/campaigns/new')}
                className="mt-4 bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]"
              >
                Create Your First Campaign
              </Button>
            </div>
          ) : (
            <table className="w-full" data-testid="campaigns-table">
              <thead className="bg-[#f5f5f5]">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Subject</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Audience</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Recipients</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Sent</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Created</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e5e5]">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-[#fafafa]">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0d0d0d] truncate max-w-[200px]" title={campaign.subject}>
                        {campaign.subject}
                      </p>
                      <p className="text-xs text-[#737373]">by {campaign.created_by_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[#525252]">
                        {getAudienceLabel(campaign.audience_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-[#737373]" />
                        <span className="text-sm text-[#0d0d0d]">{campaign.total_recipients.toLocaleString()}</span>
                      </div>
                      {campaign.status === 'sent' && (
                        <p className="text-xs text-[#737373]">
                          {campaign.sent_count} sent, {campaign.skipped_count} skipped
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(campaign.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#525252]">
                      {campaign.sent_at 
                        ? new Date(campaign.sent_at).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-[#525252]">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/campaigns/${campaign.id}`)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {campaign.status === 'draft' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                              title="Edit"
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSendCampaign(campaign.id)}
                              disabled={sending[campaign.id]}
                              className="bg-[#D4AF37] hover:bg-[#B8960F] text-[#0d0d0d]"
                              title="Send Campaign"
                            >
                              {sending[campaign.id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          </>
                        )}
                        
                        {(campaign.status === 'draft' || campaign.status === 'failed') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
